/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  XMarkIcon, 
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/solid';
import { useAuthBusiness } from '../contexts/AuthBusinessContext';
import WhatsAppService from '../services/pos/WhatsAppService';
import { db } from '../services/firebase';
import { collection, query, where, orderBy, limit, getDocs, onSnapshot } from 'firebase/firestore';

const WhatsAppAssistantWidget = ({ isEmbedded = false, isOpen: controlledIsOpen, onClose: controlledOnClose }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleClose = () => {
    if (controlledOnClose) controlledOnClose();
    else setInternalIsOpen(false);
  };
  const [isMaximized, setIsMaximized] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [operatorNeededPhones, setOperatorNeededPhones] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const { selectedBusiness, currentUserRole } = useAuthBusiness();
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!selectedBusiness?.id || !isVisible) return;
    
    // Real-time listener for operator_needed conversations (Firestore-side filtering)
    const convRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'conversations');
    const q = query(convRef, where('operator_needed', '==', true));
    
    const unsubscribe = onSnapshot(q, (snap) => {
      let count = snap.size;
      const neededPhones = new Set();
      snap.forEach(doc => {
        neededPhones.add(doc.id);
      });
      setPendingCount(count);
      setOperatorNeededPhones(neededPhones);
    }, (error) => {
      console.warn("WhatsApp Assistant listener error:", error);
    });

    return () => unsubscribe();
  }, [selectedBusiness?.id, isVisible]);

  // Visibility Check Logic
  useEffect(() => {
    const checkVisibility = async () => {
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const validRoles = ['super_admin', 'system_admin', 'owner', 'business_owner', 'manager', 'admin', 'operator', 'staff'];
        const isUserPermitted = !currentUserRole || validRoles.includes(currentUserRole);
        
        const settingsDoc = await getDoc(doc(db, 'admin_settings', 'global_settings'));
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data().whatsappSettings;
          
          if (settings) {
            const isEnabled = settings.enabled !== false;
            setIsVisible(isEnabled && isUserPermitted);
          } else {
            setIsVisible(isUserPermitted);
          }
        } else {
          setIsVisible(isUserPermitted);
        }
      } catch (err) {
        setIsVisible(true);
      }
    };

    checkVisibility();
  }, [currentUserRole]);

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadConversations();
    }
  }, [isOpen, selectedBusiness]);

  if (!isVisible) return null;

  const handleOpenConversation = (phone) => {
    handleClose();
    if (phone) {
      navigate(`/business/whatsapp?phone=${encodeURIComponent(phone)}`, { state: { selectedPhone: phone } });
    } else {
      navigate('/business/whatsapp');
    }
  };

  const loadConversations = async () => {
    if (!selectedBusiness?.id) return;
    try {
      setIsLoading(true);
      const convsRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'conversations');
      const q = query(convsRef, orderBy('updatedAt', 'desc'), limit(20));
      const snap = await getDocs(q);

      const realConvos = [];
      snap.forEach(doc => {
        const d = doc.data();
        realConvos.push({
          id: doc.id,
          phone: d.phone || d.customerPhone || doc.id,
          name: d.name || d.customerName || d.title || null,
          lastMessage: d.lastMessage || d.snippet || d.latestMessage || 'Mesaj detayı',
          timestamp: d.updatedAt || d.createdAt || d.timestamp || new Date()
        });
      });

      try {
        const rawData = await WhatsAppService.getMessages(selectedBusiness.id);
        const data = Array.isArray(rawData) ? rawData : (rawData?.data || []);
        data.forEach(msg => {
          const phone = msg.from === 'bot' ? msg.to : msg.from;
          if (phone && phone !== 'bot' && !realConvos.some(c => c.phone === phone)) {
            realConvos.push({
              id: phone,
              phone,
              name: msg.customerName || null,
              lastMessage: msg.userMessage || msg.botResponse || 'WhatsApp Mesajı',
              timestamp: msg.timestamp || new Date()
            });
          }
        });
      } catch (wsErr) {}

      realConvos.sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return timeB - timeA;
      });

      setConversations(realConvos);
      setIsLoading(false);
    } catch (err) {
      console.warn('WhatsApp Conversations load info:', err?.message || err);
      setConversations([]);
      setIsLoading(false);
    }
  };

  // Don't show the widget on the WhatsApp panel itself, or on POS/Counter/TableDetail pages
  if (
    location.pathname === '/business/whatsapp' ||
    location.pathname.includes('/pos') ||
    location.pathname.includes('/counter') ||
    location.pathname.includes('/table-detail')
  ) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      {!isOpen && controlledIsOpen === undefined && (
        <button
          onClick={() => setInternalIsOpen(true)}
          className={`whatsapp-assistant-container ${isEmbedded ? 'relative' : 'fixed bottom-[68px] right-5 md:right-4'} z-[99999] w-11 h-11 bg-white hover:bg-emerald-50 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-all duration-300 animate-bounce-soft group select-none border border-emerald-100 relative`}
          title="WhatsApp Asistanı"
        >
          {pendingCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-md animate-pulse">
              {pendingCount}
            </span>
          )}
          <svg className="w-6 h-6" viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <g id="Icons" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#67C15E">
                <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"></path>
              </g>
            </g>
          </svg>
        </button>
      )}

      {/* Conversations List Panel */}
      {isOpen && createPortal(
        <div className={`whatsapp-assistant-container ${isMaximized 
          ? "fixed inset-0 z-[100000] flex flex-col bg-white opacity-100 rounded-none w-screen h-screen shadow-2xl overflow-hidden animate-fade-in"
          : "fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-[100000] w-[calc(100vw-24px)] max-w-[380px] h-[calc(100vh-24px)] max-h-[580px] flex flex-col bg-white opacity-100 rounded-[2rem] shadow-2xl border border-slate-200 overflow-hidden animate-slide-up origin-bottom-right"}`}
        >
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white shrink-0 select-none">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-md">
                <svg className="w-6 h-6" viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg">
                  <g id="Icons" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#FFFFFF">
                      <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"></path>
                    </g>
                  </g>
                </svg>
              </div>
              <div>
                <h3 className="font-black text-sm tracking-wide">Görüşmeler</h3>
                <p className="text-[10px] text-emerald-100 font-medium opacity-80">Canlı WhatsApp Sohbetleri</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setIsMaximized(!isMaximized)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-xs font-black text-white flex items-center justify-center select-none"
                title={isMaximized ? "Küçült" : "Tam Ekran Yap"}
              >
                {isMaximized ? "🗗" : "🗖"}
              </button>
              <button 
                onClick={handleClose}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Conversations List content area */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2.5 scrollbar-hide">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full py-12 space-y-2 text-slate-400">
                <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                <div className="text-xs font-bold animate-pulse">Sohbetler yükleniyor...</div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-2">💬</div>
                <div className="text-xs font-bold text-slate-500">Henüz sohbet bulunmuyor</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Sipariş verildikçe veya mesaj geldikçe burada listelenir.</div>
              </div>
            ) : (
              conversations.map(c => {
                const isNeedsOperator = operatorNeededPhones.has(c.phone);
                return (
                <div
                  key={c.phone}
                  onClick={() => handleOpenConversation(c.phone)}
                  className={`p-3.5 ${isNeedsOperator ? 'bg-red-50/80 border-red-200 ring-1 ring-red-100' : 'bg-white hover:bg-emerald-50/50 border-slate-100 hover:border-emerald-200/50'} active:bg-emerald-100/30 rounded-2xl border transition-all duration-200 cursor-pointer flex items-center space-x-3.5 shadow-sm hover:shadow-md select-none group relative overflow-hidden`}
                >
                  {isNeedsOperator && (
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500 animate-pulse"></div>
                  )}
                  {/* Initials Avatar */}
                  <div className={`w-11 h-11 ${isNeedsOperator ? 'bg-red-100 text-red-700 border-red-200' : 'bg-emerald-100 text-emerald-700 border-emerald-50 group-hover:bg-emerald-600 group-hover:text-white'} font-extrabold text-sm rounded-full flex items-center justify-center shrink-0 border transition-colors duration-300 relative`}>
                    {c.name ? c.name.charAt(0).toUpperCase() : "M"}
                    {isNeedsOperator && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border border-white"></span>
                      </span>
                    )}
                  </div>
                  
                  {/* Name, phone, last message snippet */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between h-10 select-none">
                    <div className="flex justify-between items-start">
                      <span className={`text-xs font-extrabold truncate leading-tight transition-colors duration-300 ${isNeedsOperator ? 'text-red-800' : 'text-slate-800 group-hover:text-emerald-700'}`}>
                        {c.name || `+${c.phone}`}
                      </span>
                      {c.timestamp && (
                        <span className="text-[9px] text-slate-400 font-bold tracking-tight shrink-0">
                          {(() => {
                            try {
                              const d = c.timestamp?.toDate ? c.timestamp.toDate() : new Date(c.timestamp);
                              return isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' });
                            } catch (_) { return ''; }
                          })()}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between items-end">
                      <p className={`text-[10px] truncate max-w-[85%] leading-tight ${isNeedsOperator ? 'text-red-600 font-bold' : 'text-slate-500 group-hover:text-slate-600'}`}>
                        {c.lastMessage || 'Yeni mesaj yok'}
                      </p>
                      <span className={`text-[9px] font-black group-hover:translate-x-0.5 transition-transform ${isNeedsOperator ? 'text-red-500' : 'text-emerald-600'}`}>
                        ➔
                      </span>
                    </div>
                  </div>
                </div>
              )})
            )}
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes slide-up {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .animate-bounce-soft {
          animation: bounce-soft 2s infinite;
        }
        @keyframes bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        body.ai-assistant-open .whatsapp-assistant-container {
            opacity: 0 !important;
            pointer-events: none !important;
            transform: scale(0.9) !important;
            transition: all 0.3s ease !important;
        }
      `}</style>
    </>
  );
};

export default WhatsAppAssistantWidget;
